import { useCallback, useRef } from 'react';

const CONSENT_UPDATE_DEBOUNCE_MS = 300;

/**
 * Returns a debounced version of the callback to prevent button spamming
 * when sending consent changes to the consent management service.
 */
function useDebouncedConsentUpdate<A extends unknown[]>(
  callback: (...args: A) => void,
  delayMs: number = CONSENT_UPDATE_DEBOUNCE_MS,
): (...args: A) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  return useCallback(
    (...args: A) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        callbackRef.current(...args);
      }, delayMs);
    },
    [delayMs],
  );
}

export default useDebouncedConsentUpdate;
